import React, {useState, useEffect, useContext} from 'react';
import axios from 'axios';
import {API_URL, WHITELABEL_ID} from '../../constants';
import {Link, useNavigate, useParams} from 'react-router-dom';
import withAuth from "../../auth";
import { Breadcrumb } from 'react-bootstrap';
import moment from "moment";
import Thumbnail from "../Thumbnail";
import {AuthContext} from "../AuthContext";
import Spinner from "react-bootstrap/Spinner";
import {formatPhoneNumberWithMask} from "../../formatPhoneNumber";
import InputMask from "react-input-mask";

const DriverProfile = () => {
    const { driverId } = useParams();
    const [driver, setDriver] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingSms, setLoadingSms] = useState(false);
    const navigate = useNavigate();
    const {token, setToken, hasPermission} = useContext(AuthContext);

    const [notificationTitle, setNotificationTitle] = useState('');
    const [notificationBody, setNotificationBody] = useState('');
    const [isNotificationSent, setIsNotificationSent] = useState(false);
    const [showNotification, setShowNotification] = useState(false);

    const [smsMessage, setSmsMessage] = useState('');
    const [isSmsSent, setIsSmsSent] = useState(false);
    const [showSms, setShowSms] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [isNewPasswordSubmitted, setIsNewPasswordSubmitted] = useState(false);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_num: '',
    });
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [uploadingFile, setUploadingFile] = useState(null);

    // Fetch the details of the driver
    useEffect(() => {
        axios.get(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                setDriver(response.data.data);
            })
            .catch(error => {
                console.error('There was an error fetching the driver details!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            });
    }, [driverId]);

    useEffect(() => {
        if (driver) {
            setFormData({
                first_name: driver.first_name || '',
                last_name: driver.last_name || '',
                email: driver.email || '',
                phone_num: driver.phone_num || '',
            });
        }
    }, [driver]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async () => {
        if (!window.confirm("Are you sure that you want to update the driver's data?")) {
            return;
        }

        setErrorMessage('');
        setIsSubmitted(true);
        try {
            const response = await axios.put(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}`, formData, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            alert(response.data.message);
            setDriver(response.data.driver);
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setErrorMessage(errorMessages);
            } else if (error?.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An error occurred. Please try again later.');
            }
        }
        finally {
            setIsSubmitted(false);
        }
    };

    const handleApproveClick = () => {
        setLoading(true);
        axios.put(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/approval-status`, {},{
            params: {
                status: 'approve',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error updating the approval status!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleResetApprovalClick = () => {
        setLoading(true);
        axios.put(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/approval-status`, {},{
            params: {
                status: 'reset',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error updating the approval status!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleEnableSmsClick = () => {
        setLoadingSms(true);
        axios.put(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/sms-status`, {},{
            params: {
                status: 'enable',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error enabling SMS notifications!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoadingSms(false);
            });
    };

    const handleDisableSmsClick = () => {
        setLoadingSms(true);
        axios.put(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/sms-status`, {},{
            params: {
                status: 'disable',
            },
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert(response.data.message);
                setDriver(response.data.driver);
            })
            .catch(error => {
                console.error('There was an error disabling SMS notifications!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoadingSms(false);
            });
    };

    const handleDeleteClick = () => {
        if (window.confirm("Are you sure that you want to delete this driver?")) {
            axios.delete(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
                .then(response => {
                    alert('Driver deleted successfully!');
                    setDriver(null);
                    navigate('/drivers');
                })
                .catch(error => {
                    console.error('There was an error deleting the driver!', error);
                    if (error.response && error.response.status === 401) {
                        localStorage.removeItem('authToken');
                        setToken(null);
                        navigate('/');
                    }
                });
        }
    };

    const handleNewPasswordClick = async () => {
        if (!window.confirm("Could you please confirm if you would like to change the password for the driver?")) return false;

        setErrorMessage('');
        setIsNewPasswordSubmitted(true);
        try {
            const response = await axios.put(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/password`, {password: newPassword}, {
                headers: {'Authorization': `Bearer ${token}`}
            });
            alert(response.data.message);
            setNewPassword('');
        }
        catch (error) {
            if (error.response && error.response.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
            }
            if (error?.response?.data?.errors) {
                const errors = error.response.data.errors;
                const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                setErrorMessage(errorMessages);
            } else if (error?.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An error occurred. Please try again later.');
            }
        }
        finally {
            setIsNewPasswordSubmitted(false);
        }
    };

    const handleSendPushNotification = () => {
        if (!window.confirm("Are you sure that you want to send the push notification to this driver?")) return false;

        setErrorMessage('');
        setIsNotificationSent(true);
        axios.post(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/notify`, {title: notificationTitle, body: notificationBody}, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert('Push notification successfully sent!');
                setNotificationTitle('');
                setNotificationBody('');
            })
            .catch(error => {
                console.error('There was an error sending the push notification to the driver!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }

                if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                    setErrorMessage(errorMessages);
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setIsNotificationSent(false);
            });
    };

    const handleSendSms = () => {
        if (!window.confirm("Are you sure that you want to send the SMS to this driver?")) return false;

        setErrorMessage('');
        setIsSmsSent(true);
        axios.post(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/send-sms`, {message: smsMessage}, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        })
            .then(response => {
                alert('SMS successfully sent!');
                setSmsMessage('');
            })
            .catch(error => {
                console.error('There was an error sending the SMS to the driver!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }

                if (error?.response?.data?.errors) {
                    const errors = error.response.data.errors;
                    const errorMessages = Object.keys(errors).map(key => errors[key].join(' ')).join(' ');
                    setErrorMessage(errorMessages);
                } else if (error?.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => {
                setIsSmsSent(false);
            });
    };

    // Function to render stars based on rating
    const renderStars = (rating) => {
        const fullStars = Math.floor(rating);
        const halfStar = rating - fullStars >= 0.5;
        const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

        return (
            <>
                {[...Array(fullStars)].map((e, i) =>
                    <i key={`full-${i}`} className="fa fa-star" style={{ color: '#FFD700' }}></i>
                )}
                {halfStar && <i className="fa fa-star-half-o" style={{ color: '#FFD700' }}></i>}
                {[...Array(emptyStars)].map((e, i) =>
                    <i key={`empty-${i}`} className="fa fa-star-o" style={{ color: '#FFD700' }}></i>
                )}
                &nbsp;({rating.toFixed(1)})
            </>
        );
    };

    return (
        <div className="container-fluid mt-3">
            <Breadcrumb>
                <Breadcrumb.Item href="/drivers">Drivers</Breadcrumb.Item>
                <Breadcrumb.Item active>Driver Profile</Breadcrumb.Item>
            </Breadcrumb>
            <h1 className="text-center">Driver Profile {driver ? driver.id : ''}</h1>
            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {driver ? (
                <div className="row">
                    <div className="col-md-4">
                        <div className="card mb-3">
                            <div className="card-body" align={'center'}>
                                {driver.profile_photo &&
                                    <a href={driver.profile_photo} target="_blank" rel="noopener noreferrer">
                                        <img src={driver.profile_photo} width="100" alt={`${driver.name}'s Profile Photo`} className="mb-3" />
                                    </a>
                                }
                                {hasPermission('driver-update') ?
                                    <>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">Email:</label>
                                            <div className="col-md-9">
                                                <input type="text" className="form-control" name="email"
                                                       value={formData.email} onChange={handleChange}/>
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">Phone:</label>
                                            <div className="col-md-9">
                                                <InputMask className='form-control' mask="+9 (999) 999-9999" name="phone_num" value={formData.phone_num}
                                                           onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">First Name:</label>
                                            <div className="col-md-9">
                                                <input type="text" className="form-control" name="first_name"
                                                       value={formData.first_name} onChange={handleChange}/>
                                            </div>
                                        </div>
                                        <div className="form-group row mb-2">
                                            <label className="col-md-3 col-form-label">Last Name:</label>
                                            <div className="col-md-9">
                                                <input type="text" className="form-control" name="last_name"
                                                       value={formData.last_name} onChange={handleChange}/>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-center">
                                            <button className="btn btn-primary" onClick={handleSave}>
                                                {isSubmitted
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status"
                                                                 aria-hidden="true"/>&nbsp;Saving...</>
                                                    : "Save"
                                                }
                                            </button>
                                        </div>
                                        <hr/>
                                    </>
                                    : <p className="card-text">
                                        <b>{driver.name}</b><br/>
                                        <b>{driver.email}</b> <br/>
                                        <b>{formatPhoneNumberWithMask(driver.phone_num|| "",
                                                "+9 999 999-9999")}</b>
                                    </p>
                                }
                                {hasPermission('driver-update') && (
                                    <>
                                        <div className="d-flex align-items-center justify-content-center">
                                            {loading ? (
                                                <div className="spinner-border mr-2" role="status">
                                                    <span className="sr-only">Loading...</span>
                                                </div>
                                            ) : (
                                                <div>
                                                    {driver.admin_approved ? (
                                                        <button className="btn btn-warning" style={{marginRight: '.5em'}} onClick={handleResetApprovalClick}>Reset Approval</button>
                                                    ) : (
                                                        <button className="btn btn-success" style={{marginRight: '.5em'}} onClick={handleApproveClick}>Approve</button>
                                                    )}
                                                </div>
                                            )}

                                            {loadingSms ? (
                                                <div className="spinner-border" role="status">
                                                    <span className="sr-only">Loading...</span>
                                                </div>
                                            ) : (
                                                <div>
                                                    {driver.sms_enabled ? (
                                                        <button
                                                            className="btn btn-warning"
                                                            style={{ marginRight: '.5em' }}
                                                            onClick={handleDisableSmsClick}
                                                        >
                                                            <i class="fa fa-comments"/>&nbsp; Disable SMS
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="btn btn-success"
                                                            style={{ marginRight: '.5em' }}
                                                            onClick={handleEnableSmsClick}
                                                        >
                                                            <i className="fa fa-comments"/>&nbsp;Enable SMS
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <hr/>
                                        <div className="form-row align-items-center justify-content-center">
                                            <div className="col-auto">
                                                <input type="password" className="form-control mb-2" name="password" placeholder="New password"
                                                       value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password"/>
                                            </div>
                                            <div className="col-auto">
                                                <button className="btn btn-dark mb-2" onClick={handleNewPasswordClick}>
                                                    {isNewPasswordSubmitted
                                                        ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Changing...</>
                                                        : "Change"
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                        <hr/>
                                        <button className="btn btn-danger" onClick={handleDeleteClick}>Delete</button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <VehicleList driverId={driverId} />

                        {hasPermission('driver-update') && (
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5
                                        className="text-center"
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => setShowNotification(!showNotification)}
                                    >
                                        Send Push Notification {showNotification ? '▼' : '▶'}
                                    </h5>
                                    <div
                                        className={`fade-section ${showNotification ? 'show' : 'hide'}`}
                                    >
                                        <div className="form-group mb-2">
                                            <input className="form-control" type="text" placeholder="Title" value={notificationTitle}
                                                   onChange={(e) => setNotificationTitle(e.target.value)} />
                                        </div>
                                        <div className="form-group mb-2">
                                            <textarea className="form-control" style={{minHeight: '50px'}} placeholder="Body" value={notificationBody}
                                                      onChange={(e) => setNotificationBody(e.target.value)} />
                                        </div>
                                        <div align={'center'}>
                                            <button className="btn btn-primary"onClick={handleSendPushNotification}>
                                                {isNotificationSent
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Sending...</>
                                                    : 'Send'
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {hasPermission('driver-update') && (
                            <div className="card mb-3">
                                <div className="card-body">
                                    <h5
                                        className="text-center"
                                        style={{ cursor: 'pointer', color: '#007bff' }}
                                        onClick={() => setShowSms(!showSms)}
                                    >
                                        Send SMS {showSms ? '▼' : '▶'}
                                    </h5>
                                    <div
                                        className={`fade-section ${showSms ? 'show' : 'hide'}`}
                                    >
                                        <div className="form-group mb-2">
                                            <textarea className="form-control" style={{minHeight: '50px'}} placeholder="Message" value={smsMessage}
                                                      onChange={(e) => setSmsMessage(e.target.value)} />
                                        </div>
                                        <div align={'center'}>
                                            <button className="btn btn-primary" onClick={handleSendSms}>
                                                {isSmsSent
                                                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />&nbsp;Sending...</>
                                                    : 'Send'
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="col-md-4">
                        <div className="card mb-3">
                            <div className="card-body">
                                <h5 className="text-center">Details</h5>
                                <p className="card-text">
                                    Average Rating: <b>{renderStars(driver.avg_rating || 0)}</b>
                                </p>
                                <p className="card-text">
                                    Registered: <b>{moment(driver.created_at).format('YYYY-MM-DD HH:mm')}</b>
                                    <br/>
                                    Last Login: <b>{driver.last_login_at ? moment(driver.last_login_at).format('YYYY-MM-DD HH:mm') : 'unknown '}</b>
                                    <br/>
                                    IP: <b>{driver.last_login_ip || 'unknown'}</b>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="d-flex justify-content-center align-items-center" style={{height: '80vh'}}>
                    <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                    </div>
                </div>
            )}

            <style jsx> {`
                .fade-section {
                  max-height: 0;
                  opacity: 0;
                  overflow: hidden;
                  transition: max-height 0.4s ease-in-out, opacity 0.4s ease-in-out;
                }
                
                .fade-section.show {
                  max-height: 500px;
                  opacity: 1;
                }
                
                .fade-section.hide {
                  max-height: 0;
                  opacity: 0;
                }            
            `}
            </style>
        </div>
    );
};

const VehicleList = ({driverId}) => {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {token, setToken} = useContext(AuthContext);

    // Fetch vehicles of the driver
    useEffect(() => {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        axios.get(`${API_URL}/api/wl/${WHITELABEL_ID}/manager/drivers/${driverId}/vehicles`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            })
            .then(response => {
                setVehicles(response.data.data);
            })
            .catch(error => {
                console.error('There was an error fetching the vehicles!', error);
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem('authToken');
                    setToken(null);
                    navigate('/');
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, [driverId]);

    return (
        <div className="card mb-3">
            <div className="card-body">
                <h5 className="text-center">Vehicles</h5>
                {loading ? (
                    <div className="d-flex justify-content-center mb-2">
                        <div className="spinner-border" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered text-center">
                            <thead>
                            <tr>
                                <th>ID / Make / Model / License</th>
                                <th>Truck Type</th>
                                <th>Drivers</th>
                            </tr>
                            </thead>
                            <tbody>
                            {vehicles.map(vehicle => (
                                <tr key={vehicle.id}>
                                    <td>
                                        <Link to={`/vehicles/${vehicle.id}`} target="_blank">
                                            <b>{vehicle.id} {vehicle.make} {vehicle.model} {vehicle.plate_number}</b>
                                        </Link>
                                    </td>
                                    <td>{vehicle.truckType?.title}</td>
                                    <td>
                                        {vehicle.drivers && vehicle.drivers.length > 0 ? (
                                            vehicle.drivers.map((driver, index) => (
                                                 <div key={index}>
                                                     {driver.type !== 2 &&
                                                        <Link to={`/drivers/${driver.id}`} target="_blank">
                                                            <b>{driver.id} {driver.name}</b>
                                                        </Link>}
                                                     {driver.type === 2 && 'No Driver'}
                                                </div>

                                            ))
                                        ) : (
                                            <span>No Driver</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div align={'center'}>
                    <Link to="/vehicles/create" state={{ driverId: driverId }} className="btn btn-primary">
                        Add Vehicle
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default withAuth(DriverProfile);
